import { matchPath, useLocation } from 'react-router-dom';
import { TaskDto } from '@sib/api';
import { useCan } from '@sib/auth';
import { NO_DATA_PLACEHOLDER } from '@sib/config';
import { useTranslation } from '@sib/i18n';
import { Button, Person, Pill, TicketNumberBadge } from '@sib/ui';
import { Dot } from '@sib/ui/icons';
import {
  DetailsField,
  useTicketDetailsModalActions,
  RelatedTaskBanner,
} from '@sidecar/common/components';
import { paths } from '@sidecar/config/paths';
import { permissions } from '@sidecar/config/permissions';
import { useTaskDetailsTabContent } from './TaskDetailsTabContent.utils';

interface TaskDetailsTabContentProps {
  task: TaskDto;
}

export const TaskDetailsTabContent = ({ task }: TaskDetailsTabContentProps) => {
  const { t } = useTranslation();
  const {
    title,
    description,
    organizationName,
    status,
    priority,
    category,
    assigneeUser,
    createdByUser,
    estimatedTime,
    vendorName,
    dueDate,
    lastUpdated,
    completedSubtasks,
    relatedTicketNumber,
    relatedTicketId,
  } = useTaskDetailsTabContent(task);
  const { openTicket } = useTicketDetailsModalActions();
  const { pathname } = useLocation();
  const isGlobalWorkRoute =
    matchPath({ path: `${paths.work.root}/*`, end: false }, pathname) !== null;
  const can = useCan({ scope: isGlobalWorkRoute ? 'global' : 'organization' });
  const canViewTicket = can({ permissions: permissions.tickets.view });

  return (
    <div>
      <DetailsField
        variant="block"
        className="pt-0"
        label={t('components.taskForm.fields.title.label')}
      >
        {title}
      </DetailsField>

      <DetailsField
        variant="block"
        label={t('components.taskForm.fields.description.label')}
      >
        {description}
      </DetailsField>

      <div className="grid w-full grid-cols-1 sm:grid-cols-2 sm:gap-x-8">
        <DetailsField
          label={t('components.taskForm.fields.organization.label')}
        >
          {organizationName}
        </DetailsField>
        <DetailsField label={t('components.tasksTable.columns.status')}>
          <Dot style={{ color: status.color }} />
          {status.text}
        </DetailsField>
        <DetailsField label={t('components.taskForm.fields.priority.label')}>
          {priority.hasValue ? (
            <>
              <span className="inline-flex" style={{ color: priority.color }}>
                {priority.icon}
              </span>
              {priority.text}
            </>
          ) : (
            NO_DATA_PLACEHOLDER
          )}
        </DetailsField>
        <DetailsField label={t('components.taskForm.fields.category.label')}>
          {category.hasValue ? (
            <Pill color={category.color} text={category.label} />
          ) : (
            NO_DATA_PLACEHOLDER
          )}
        </DetailsField>
        <DetailsField label={t('components.taskForm.fields.assignedTo.label')}>
          {assigneeUser ? (
            <Person
              name={assigneeUser.name}
              email={assigneeUser.email}
              avatarUrl={assigneeUser.avatarUrl}
            />
          ) : (
            t('unassigned')
          )}
        </DetailsField>
        <DetailsField label={t('components.taskForm.fields.author.label')}>
          {createdByUser ? (
            <Person
              name={createdByUser.name}
              email={createdByUser.email}
              avatarUrl={createdByUser.avatarUrl}
            />
          ) : (
            NO_DATA_PLACEHOLDER
          )}
        </DetailsField>
        <DetailsField label={t('components.tasksTable.columns.estimatedTime')}>
          {estimatedTime}
        </DetailsField>
        <DetailsField label={t('components.taskForm.fields.vendor.label')}>
          {vendorName}
        </DetailsField>
        <DetailsField label={t('components.taskForm.fields.deadline.label')}>
          {dueDate}
        </DetailsField>
        <DetailsField label={t('components.tasksTable.columns.lastUpdated')}>
          {lastUpdated}
        </DetailsField>
        <DetailsField
          label={t('components.taskDetails.fields.completedSubtasks')}
        >
          {completedSubtasks}
        </DetailsField>
        <DetailsField label={t('components.taskForm.fields.ticket.label')}>
          {relatedTicketNumber ? (
            <div className="flex w-full items-center gap-4">
              <TicketNumberBadge ticketNumber={relatedTicketNumber} />
              {relatedTicketId && canViewTicket && (
                <Button
                  type="button"
                  variant="link"
                  className="text-primary h-auto shrink-0 p-0 text-sm font-semibold underline"
                  onClick={() => openTicket(relatedTicketId)}
                >
                  {t('components.taskDetails.fields.viewTicket')}
                </Button>
              )}
            </div>
          ) : (
            NO_DATA_PLACEHOLDER
          )}
        </DetailsField>
        <RelatedTaskBanner className="mt-4" task={task} />
      </div>
    </div>
  );
};
